import type { FC, ReactNode, Ref, RefAttributes } from "react";
import type { SelectProps as AriaSelectProps } from "react-aria-components";

import { ChevronDown } from "@untitledui/icons";
import { isValidElement } from "react";
import {
	Button as AriaButton,
	ListBox as AriaListBox,
	Select as AriaSelect,
	SelectValue as AriaSelectValue,
} from "react-aria-components";

import { Avatar } from "@/components/ui/avatar/avatar";
import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";

import { ComboBox } from "./combobox";
import { Popover } from "./popover";
import { SelectContext } from "./select-context";
import { SelectItem } from "./select-item";
import { sizes } from "./select-sizes";

export interface CommonProps {
	hint?: string;
	label?: string;
	placeholder?: string;
	size?: "md" | "sm";
	tooltip?: string;
}

export type SelectItemType = {
	avatarUrl?: string;
	description?: string;
	icon?: FC | ReactNode;
	id: string;
	isDisabled?: boolean;
	label?: string;
	supportingText?: string;
};

interface SelectProps
	extends
		CommonProps,
		Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement> {
	children: ((item: SelectItemType) => ReactNode) | ReactNode;
	errorMessage?: string;
	items?: SelectItemType[];
	placeholderIcon?: FC | ReactNode;
	popoverClassName?: string;
}

interface SelectValueProps {
	isDisabled: boolean;
	isFocused: boolean;
	isOpen: boolean;
	placeholder?: string;
	placeholderIcon?: FC | ReactNode;
	ref?: Ref<HTMLButtonElement>;
	size: "md" | "sm";
}

const SelectValue = ({
	isDisabled,
	isFocused,
	isOpen,
	placeholder,
	placeholderIcon,
	ref,
	size,
}: SelectValueProps) => {
	return (
		<AriaButton
			className={cx(
				"relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset",
				(isFocused || isOpen) && "ring-2 ring-brand",
				isDisabled && "cursor-not-allowed opacity-50",
			)}
			ref={ref}
		>
			<AriaSelectValue<SelectItemType>
				className={cx(
					"flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle",

					// Icon styles
					"*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary",

					sizes[size].root,
				)}
			>
				{(state) => {
					const selectedItem = state.selectedItems[0];
					const Icon = selectedItem?.icon || placeholderIcon;
					return (
						<>
							{selectedItem?.avatarUrl ? (
								<Avatar
									alt={selectedItem.label}
									size="xs"
									src={selectedItem.avatarUrl}
								/>
							) : isReactComponent(Icon) ? (
								<Icon aria-hidden="true" data-icon />
							) : isValidElement(Icon) ? (
								Icon
							) : null}

							{selectedItem ? (
								<span className="flex w-full gap-2 truncate">
									<span className="truncate text-md font-medium text-primary">
										{selectedItem.label}
									</span>
									{selectedItem.supportingText && (
										<span className="text-md text-tertiary">
											{selectedItem.supportingText}
										</span>
									)}
								</span>
							) : (
								<span className="text-md text-placeholder">{placeholder}</span>
							)}

							<ChevronDown
								aria-hidden="true"
								className={cx(
									"ml-auto shrink-0 text-fg-quaternary",
									size === "sm" ? "size-4 stroke-[2.5px]" : "size-5",
								)}
							/>
						</>
					);
				}}
			</AriaSelectValue>
		</AriaButton>
	);
};

const Select = ({
	children,
	className,
	errorMessage,
	hint,
	items,
	label,
	placeholder = "Select",
	placeholderIcon,
	size = "sm",
	tooltip,
	...rest
}: SelectProps) => {
	return (
		<SelectContext.Provider value={{ size }}>
			<AriaSelect
				{...rest}
				className={(state) =>
					cx(
						"flex flex-col gap-1.5",
						typeof className === "function" ? className(state) : className,
					)
				}
			>
				{(state) => (
					<>
						{label && (
							<Label isRequired={state.isRequired} tooltip={tooltip}>
								{label}
							</Label>
						)}

						<SelectValue
							{...state}
							{...{ placeholder, size }}
							placeholderIcon={placeholderIcon}
						/>

						<Popover className={rest.popoverClassName} size={size}>
							<AriaListBox className="size-full outline-hidden" items={items}>
								{children}
							</AriaListBox>
						</Popover>

						{hint && <HintText isInvalid={state.isInvalid}>{hint}</HintText>}
						{errorMessage && state.isInvalid && (
							<HintText isInvalid={state.isInvalid}>{errorMessage}</HintText>
						)}
					</>
				)}
			</AriaSelect>
		</SelectContext.Provider>
	);
};

const _Select = Select as typeof Select & {
	ComboBox: typeof ComboBox;
	Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
